import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { SortDirection, buildPagedSortQueryParams } from '../utils/pagedSortQueryParams';
import { IVolunteer, IVolunteerFilter, IVolunteersFilterRequest, isVolunteerBoardDisabledError } from './models';

export const VOLUNTEERS_KEY = '/volunteering/volunteers/filter';

export type VolunteerSortField = 'lastActivityDate' | 'totalHours';

export const useVolunteersQuery = ({
	filter = {},
	excludeActivityIds,
	sortBy = 'lastActivityDate',
	sort = 'desc',
	pageSize = 25,
	enabled = true,
	impersonationContext,
}: {
	filter?: IVolunteerFilter;
	excludeActivityIds?: string[];
	sortBy?: VolunteerSortField;
	sort?: SortDirection;
	pageSize?: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
} = {}) => {
	const userSession = useUserSession();
	return useInfiniteQuery({
		enabled,
		queryKey: [VOLUNTEERS_KEY, filter, excludeActivityIds, sortBy, sort, pageSize, impersonationContext?.account?.id],
		queryFn: async ({ pageParam }) => {
			const body: IVolunteersFilterRequest = { filter, excludeActivityIds };
			const value = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<IVolunteer>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'volunteering/volunteers/filter',
					queryParams: buildPagedSortQueryParams({ pageSize, pageToken: pageParam, sortBy, sort }),
				}),
				'POST',
				body
			);
			return value ?? { values: [] as IVolunteer[] };
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		initialPageParam: '',
		meta: {
			toastErrorMessage: (error: Error) => (isVolunteerBoardDisabledError(error) ? '' : 'Failed to load volunteers'),
		},
	});
};
